// Laporan global — formatter murni (deterministik, mudah diuji).

import { WIB_TIME_ZONE } from "@/lib/wib";

const BULAN_SINGKAT = [
  "Jan", "Feb", "Mar", "Apr", "Mei", "Jun",
  "Jul", "Agu", "Sep", "Okt", "Nov", "Des",
] as const;

const BULAN_PANJANG = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
] as const;

/** "Rp 1.500.000" — 0 tampil "Rp 0" (bukan "—"). Membulatkan. */
export function formatRp(n: number): string {
  return `Rp ${Math.round(n).toLocaleString("id-ID")}`;
}

/** "1.234" — angka bulat dengan pemisah ribuan id-ID. */
export function formatInt(n: number): string {
  return Math.round(n).toLocaleString("id-ID");
}

/**
 * "12,3%" dari fraksi 0..1 (koma desimal id-ID, 1 angka di belakang koma).
 * Input non-hingga → "0%".
 */
export function formatPct(fraction: number): string {
  if (!Number.isFinite(fraction)) return "0%";
  return `${(fraction * 100).toFixed(1).replace(".", ",")}%`;
}

/**
 * Komponen tanggal di kalender WIB (Asia/Jakarta) EKSPLISIT untuk ISO
 * manapun — bukan getter lokal proses (`getDate()`/`getMonth()`), yang bisa
 * berarti UTC server alih-alih WIB begitu kode ini kebetulan dieksekusi di
 * server (Next.js men-SSR Client Component sebelum hydration). Tanpa ini,
 * rentang yang sudah benar WIB dari `@/lib/reports/dateRange` bisa tampil
 * meleset sehari lagi tepat di titik terakhir sebelum dirender (bughunt
 * I2-zona-waktu, temuan I2-2/I2-5).
 */
function wibDateParts(iso: string): { day: number; month: number; year: number } | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: WIB_TIME_ZONE,
    day: "numeric",
    month: "numeric",
    year: "numeric",
  }).formatToParts(d);
  const pick = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { day: pick("day"), month: pick("month") - 1, year: pick("year") };
}

/** "5 Jun 2026" dari ISO (kalender WIB). Input kosong/invalid → "—". */
export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const p = wibDateParts(iso);
  if (!p) return "—";
  return `${p.day} ${BULAN_SINGKAT[p.month]} ${p.year}`;
}

/** "5 Juni 2026" — bentuk panjang untuk header/label rentang. */
export function formatLongDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const p = wibDateParts(iso);
  if (!p) return "—";
  return `${p.day} ${BULAN_PANJANG[p.month]} ${p.year}`;
}

const HARI_SINGKAT = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"] as const;

/**
 * "Rab, 15 Okt 2026" dari 'yyyy-mm-dd' — label baris rekap PER HARI.
 *
 * Masukannya tanggal KALENDER yang sudah dihitung di WIB (mis. `isoToWibYmd`),
 * bukan instan ISO: nama harinya dihitung lewat `Date.UTC`, jadi tak ada zona
 * waktu proses yang bisa menggesernya. Bukan tanggal nyata → "—".
 */
export function formatDayDate(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return "—";
  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);
  const d = new Date(Date.UTC(year, month, day));
  // 2026-02-30 tak ditolak Date.UTC — ia bergulir ke 2 Mar; tolak di sini.
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month || d.getUTCDate() !== day) {
    return "—";
  }
  return `${HARI_SINGKAT[d.getUTCDay()]}, ${day} ${BULAN_SINGKAT[month]} ${year}`;
}
